/**
 * CompanyPanels
 *
 * Agrupa en un solo archivo todos los paneles laterales relacionados
 * con empresas: visualización (read-only) y creación/edición.
 *
 * Uso en Dashboard:
 *   const companyPanels = useCompanyPanels();
 *   ...
 *   companyPanels.openDetail(company)   // botón "Ver"
 *   companyPanels.openEdit(company)     // botón "Editar"
 *   companyPanels.openCreate()          // botón "Nueva Empresa"
 *   ...
 *   <CompanyPanels hook={companyPanels} users={users} onSave={loadData} />
 */

import { useState } from "react";
import { type Company } from "@/services/companiesService";
import { type User } from "@/services/usersService";
import CompanyDetailModal from "@/components/CompanyDetailModal";
import CompanyFormSheet from "@/components/CompanyFormSheet";

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useCompanyPanels() {
  // Panel de detalle (solo lectura)
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailCompany, setDetailCompany] = useState<Company | null>(null);

  // Panel de formulario (crear / editar)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formCompany, setFormCompany] = useState<Company | null>(null);

  const openDetail = (company: Company) => {
    setDetailCompany(company);
    setIsDetailOpen(true);
  };

  const openEdit = (company: Company) => {
    setFormCompany(company);
    setIsFormOpen(true);
  };

  const openCreate = () => {
    setFormCompany(null);
    setIsFormOpen(true);
  };

  return {
    // Estado interno — usado por <CompanyPanels />
    _state: { isDetailOpen, detailCompany, isFormOpen, formCompany },
    _setters: { setIsDetailOpen, setIsFormOpen },
    // Handlers públicos — usados por Dashboard
    openDetail,
    openEdit,
    openCreate,
  };
}

// ─── Componente ──────────────────────────────────────────────────────────────

interface CompanyPanelsProps {
  hook: ReturnType<typeof useCompanyPanels>;
  /** Lista de usuarios necesaria para resolver el dueño en el panel de detalle */
  users: User[];
  /** Se llama tras crear / editar / cambiar estado para recargar datos */
  onSave: () => void;
}

export function CompanyPanels({ hook, users, onSave }: CompanyPanelsProps) {
  const { _state: s, _setters: set } = hook;

  const owner = s.detailCompany
    ? (users.find(
        (u) => u.role_id === 2 && u.company_id === s.detailCompany!.id
      ) ?? null)
    : null;

  return (
    <>
      {/* Panel de visualización */}
      <CompanyDetailModal
        isOpen={s.isDetailOpen}
        onClose={() => set.setIsDetailOpen(false)}
        company={s.detailCompany}
        owner={owner}
      />

      {/* Panel de creación / edición */}
      <CompanyFormSheet
        isOpen={s.isFormOpen}
        onClose={() => set.setIsFormOpen(false)}
        company={s.formCompany}
        onSave={onSave}
        onDelete={onSave}
      />
    </>
  );
}
